import { endpoints } from '@api'
import { useToast } from '@chakra-ui/react'
import { formatTranscript } from '@lib/transcript'
import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from 'react-query'

interface UseSessionTranscriptOptions {
  /**
   * Start the query only when enabled is true (useful for lazy-loaded UIs like modals)
   */
  enabled?: boolean
  /**
   * Pass the server response you might already have so the hook doesn't re-fetch unnecessarily
   */
  initialData?: any
}

export const useSessionTranscript = (
  sessionId: string,
  /** Used so we can invalidate the canGenerateTxPlan cache on delete */
  clientId?: string | number,
  options: UseSessionTranscriptOptions = {}
) => {
  const { enabled = true, initialData } = options
  const queryClient = useQueryClient()
  const toast = useToast()

  const {
    data: sessionTranscriptData,
    isLoading: isLoadingTranscript,
    isFetching: isFetchingTranscript
  } = useQuery(
    [endpoints.getSessionTranscript.getCacheId(), sessionId],
    () => endpoints.getSessionTranscript.request({ sessionId }),
    {
      enabled,
      initialData,
      staleTime: Infinity, // Never consider stale once we have data
      cacheTime: Infinity, // Keep in cache indefinitely so second modal open is instant
      refetchInterval: data => {
        const transcript = data?.sessionTranscript
        // Keep polling while the transcript is still processing (no URL yet)
        if (transcript && (!transcript.url || transcript.isLoading)) {
          return 5000
        }
        return false
      }
    }
  )

  // Separate query for the actual transcript text
  const { data: transcriptData, isLoading: isLoadingTranscriptText } = useQuery(
    [
      'transcript-text',
      sessionId,
      sessionTranscriptData?.sessionTranscript?.url
    ],
    async () => {
      const url = sessionTranscriptData?.sessionTranscript?.url
      if (!url) return null
      const res = await axios.get(url)
      return formatTranscript(res?.data?.results?.utterances || [])
    },
    {
      enabled: enabled && !!sessionTranscriptData?.sessionTranscript?.url,
      staleTime: Infinity, // Never refetch once we have the data
      cacheTime: Infinity // Keep in cache indefinitely
    }
  )

  const { mutate: deleteSessionTranscript, isLoading: isDeletingTranscript } =
    useMutation(endpoints.deleteSessionTranscript.request, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          endpoints.getSessionTranscript.getCacheId(),
          sessionId
        ])
        // Remove the transcript text from cache
        queryClient.removeQueries(['transcript-text', sessionId])
        if (clientId) {
          queryClient.invalidateQueries([
            // @ts-expect-error – existing codebase stores this way
            endpoints.canGenerateTxPlan.getCacheId(clientId)
          ])
        }
        queryClient.setQueryData(
          [endpoints.getSessionTranscript.getCacheId(), sessionId],
          () => ({ sessionTranscript: null })
        )
        toast({
          description: 'Transcript deleted!',
          status: 'success',
          isClosable: true,
          duration: 2000
        })
      }
    })

  const sessionTranscript = sessionTranscriptData?.sessionTranscript
  const isUnavailable = !sessionTranscript

  // Case 1: initial fetch / background refetch
  const isFetching = isLoadingTranscript || isFetchingTranscript

  // Case 2: transcription object exists but we still don't have a URL → still processing
  const isProcessing = !!sessionTranscript && !sessionTranscript.url

  // Case 3: we have a URL but still need to fetch & format the transcript text
  const isFetchingTranscriptText =
    !!sessionTranscript?.url && isLoadingTranscriptText

  // Determine if the transcript is fully loaded (we have text and a URL regardless of background refetches)
  const isLoaded = !!sessionTranscript?.url && !!transcriptData

  // Show loading spinner only when the transcript is NOT yet loaded
  const isLoading =
    !isLoaded &&
    (isFetching || sessionTranscript?.isLoading || isFetchingTranscriptText)

  return {
    sessionTranscript,
    transcript: transcriptData || [],
    isUnavailable,
    isLoading,
    isProcessing,
    isLoaded,
    deleteSessionTranscript,
    isDeletingTranscript
  }
}
